import React from 'react'
import {useCallback, useEffect, useState} from 'react'
import {Button, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useTelephony} from '../../hooks/useTelephony'
import {
  TELEPHONY_DRAWER_STATE_EVENT,
  TELEPHONY_DRAWER_TOGGLE_EVENT,
  TelephonyDrawerStateDetail,
} from '../../types/telephony'

const floatingToolsStyle: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  right: 0,
  transform: 'translateY(-50%)',
  zIndex: 1051,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}

const floatingTabStyle: React.CSSProperties = {
  borderRadius: '10px 0 0 10px',
  padding: '8px 12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
}

export const TelephonyFloatingButton: React.FC = () => {
  const telephony = useTelephony()
  const [drawerState, setDrawerState] = useState<TelephonyDrawerStateDetail>({
    mounted: false,
    visible: false,
    provider: undefined,
  })
  const tooltipLabel =
    drawerState.mounted && drawerState.provider === 'VICIDIAL'
      ? drawerState.visible
        ? 'Hide VICIdial Webphone'
        : 'Show VICIdial Webphone'
      : 'Open VICIdial Webphone'

  useEffect(() => {
    const handleDrawerState = (event: Event) => {
      const customEvent = event as CustomEvent<TelephonyDrawerStateDetail>
      if (!customEvent.detail) return
      setDrawerState(customEvent.detail)
    }

    window.addEventListener(TELEPHONY_DRAWER_STATE_EVENT, handleDrawerState as EventListener)

    return () => {
      window.removeEventListener(TELEPHONY_DRAWER_STATE_EVENT, handleDrawerState as EventListener)
    }
  }, [])

  const handleOpenVicidialWebphone = useCallback(async () => {
    if (drawerState.mounted && drawerState.provider === 'VICIDIAL') {
      window.dispatchEvent(new CustomEvent(TELEPHONY_DRAWER_TOGGLE_EVENT))
      return
    }

    if (telephony.vicidialSession.activeSession) {
      await telephony.openWebphone('VICIDIAL', {preferAgentSession: false})
      return
    }

    if (telephony.vicidialSession.connected || telephony.vicidialSession.connecting) {
      await telephony.openWebphone('VICIDIAL', {preferAgentSession: true})
      return
    }

    await telephony.connectVicidialSession()
  }, [drawerState.mounted, drawerState.provider, telephony])

  if (!telephony.config.vicidial_enabled) {
    return null
  }

  return (
    <div style={floatingToolsStyle} id="global-telephony-floating-tools">
      <OverlayTrigger
        placement='left'
        overlay={<Tooltip id='telephony-vicidial-webphone-tooltip'>{tooltipLabel}</Tooltip>}
      >
        <Button
          style={floatingTabStyle}
          variant={
            telephony.vicidialSession.status === 'INCALL'
              ? 'success'
              : drawerState.mounted && drawerState.visible
              ? 'dark'
              : telephony.vicidialSession.connected
              ? 'primary'
              : 'light'
          }
          title={tooltipLabel}
          onClick={handleOpenVicidialWebphone}
          disabled={!telephony.vicidialSession.connected && !telephony.vicidialSession.connecting}
        >
          <i className='bi bi-telephone fs-4' />
        </Button>
      </OverlayTrigger>
    </div>
  )
}
